<template>
  <div class="app">
    <div class="row h-100">
      <div class="col-4">
        <div class="row h-100">
          <div class="col-12">
            <Profile :resource="resource" />
          </div>
          <div class="col-12 align-self-end">
            <Calendar
              ref="calendar"
              :resource="resource"
              @select-month="onSelectMonth"
              @select-date="onSelectDate"
            />
          </div>
        </div>
      </div>
      <div class="col-4 h-100">
        <Status ref="status" :resource="resource" />
      </div>
      <div class="col-4 h-100">
        <Events
          ref="events"
          :date="selectedDate"
          :resource="resource"
          @scroll="onScrollEvents"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Profile from "./components/Profile.vue";
import Calendar from "./components/Calendar.vue";
import Status from "./components/Status.vue";
import Events from "./components/Events.vue";

let resetTimeout = null;

export default {
  components: {
    Profile,
    Calendar,
    Status,
    Events,
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: new Date(),
      resource: this.getUrlParameter("?resource", null),
      pollingInterval: null,
      visibilityChangeHandler: null,
      focusHandler: null,
      blurHandler: null,
    };
  },
  created() {
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
    // Remove event listeners only when component is being destroyed
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      if (this.focusHandler) {
        window.removeEventListener("focus", this.focusHandler);
        this.focusHandler = null;
        // console.log(
        //   "[Resource Widget] ✅ Focus listener removed (component unmounting)"
        // );
      }
      if (this.blurHandler) {
        window.removeEventListener("blur", this.blurHandler);
        this.blurHandler = null;
        // console.log(
        //   "[Resource Widget] ✅ Blur listener removed (component unmounting)"
        // );
      }
      if (this.visibilityChangeHandler) {
        document.removeEventListener(
          "visibilitychange",
          this.visibilityChangeHandler
        );
        this.visibilityChangeHandler = null;
        // console.log(
        //   "[Resource Widget] ✅ Visibility listener removed (component unmounting)"
        // );
      }
    }
  },
  methods: {
    onSelectMonth(date) {
      this.startResetTimeout();
    },
    onSelectDate(date) {
      this.selectedDate = date;
      this.startResetTimeout();
    },
    onScrollEvents() {
      this.startResetTimeout();
    },
    reset() {
      this.$refs.calendar.reset();
    },
    startResetTimeout() {
      clearTimeout(resetTimeout);
      resetTimeout = setTimeout(() => {
        this.reset();
      }, 2 * 60 * 1000);
    },
    getUrlParameter(parameter, defaultvalue) {
      let urlParameter = defaultvalue;

      let pathArray = window.location.pathname.split("/");
      var pathResource = pathArray[pathArray.length - 1];

      if (window.location.href.indexOf(parameter) > -1) {
        // support for classic/legacy URLs with GET parameter
        urlParameter = this.getUrlParameters()["resource"];
      } else if (pathResource !== "") {
        urlParameter = pathResource;
      }

      return urlParameter;
    },
    getUrlParameters() {
      let parameters = [];
      window.location.href.replace(
        /[?&]+([^=&]+)=([^&]*)/gi,
        (m, key, value) => {
          parameters[key] = value;
        }
      );

      return parameters;
    },
    startPolling() {
      // Only poll when tab is focused (performance optimization for 500+ tabs)
      if (typeof document === "undefined") return;

      // Stop any existing polling first
      this.stopPolling();

      // Remove old listeners if they exist (to prevent duplicates)
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        if (this.focusHandler) {
          window.removeEventListener("focus", this.focusHandler);
        }
        if (this.blurHandler) {
          window.removeEventListener("blur", this.blurHandler);
        }
        if (this.visibilityChangeHandler) {
          document.removeEventListener(
            "visibilitychange",
            this.visibilityChangeHandler
          );
        }
      }

      // Check if page is currently visible (use visibility API, more reliable than hasFocus)
      const isVisible = !document.hidden;
      const visibilityState = document.visibilityState || "unknown";

      // console.log("[Resource Widget] startPolling called", {
      //   isVisible,
      //   documentHidden: document.hidden,
      //   visibilityState: visibilityState,
      //   hasFocus: document.hasFocus(), // Just for logging, not used in logic
      // });

      // Handler for when window gains focus (user returns to browser from another app)
      // This works independently from tab visibility - both can trigger refresh
      const onFocus = () => {
        const nowVisible = !document.hidden;

        // console.log(
        //   "[Resource Widget] 🔵 WINDOW FOCUS event triggered (returned to browser)",
        //   {
        //     nowVisible,
        //     documentHidden: document.hidden,
        //     visibilityState: document.visibilityState,
        //   }
        // );

        if (nowVisible) {
          // Window gained focus and tab is visible - trigger immediate refresh
          // console.log(
          //   "[Resource Widget] ✅ Window focused + tab visible - refreshing components NOW"
          // );
          this.reloadAllComponents();
          // Resume polling
          this.startPolling();
        } else {
          // Window focused but tab not visible - wait for visibility change
          // console.log(
          //   "[Resource Widget] ⚠️ Window focused but tab not visible - will refresh when tab becomes visible"
          // );
        }
      };

      // Handler for when window loses focus (user switches to another application)
      const onBlur = () => {
        const nowVisible = !document.hidden;

        // console.log(
        //   "[Resource Widget] 🔴 WINDOW BLUR event triggered (switched to another app)",
        //   {
        //     nowVisible,
        //     documentHidden: document.hidden,
        //     visibilityState: document.visibilityState,
        //   }
        // );

        // Stop polling when window loses focus (even if tab is still visible)
        // This saves resources when user switches to another application
        // console.log("[Resource Widget] ⛔ Window blurred - stopping polling");
        this.stopPolling();
      };

      // Handler for tab visibility changes (user switches tabs)
      // This works independently from window focus - both can trigger refresh
      this.visibilityChangeHandler = () => {
        const nowVisible = !document.hidden;

        // console.log("[Resource Widget] 👁️ TAB VISIBILITY CHANGE event", {
        //   nowVisible,
        //   documentHidden: document.hidden,
        //   visibilityState: document.visibilityState,
        // });

        if (nowVisible) {
          // Tab became visible - trigger immediate refresh and start polling
          // This handles: tab switching, browser minimize/restore, etc.
          // console.log(
          //   "[Resource Widget] ✅ Tab visible - refreshing components NOW"
          // );
          this.reloadAllComponents();
          this.startPolling();
        } else {
          // Tab became hidden - stop polling
          // console.log("[Resource Widget] ⛔ Tab hidden - stopping polling");
          this.stopPolling();
        }
      };

      // Set up event listeners
      window.addEventListener("focus", onFocus);
      window.addEventListener("blur", onBlur);
      document.addEventListener(
        "visibilitychange",
        this.visibilityChangeHandler
      );

      // console.log("[Resource Widget] Event listeners attached");

      // Store handlers for cleanup
      this.focusHandler = onFocus;
      this.blurHandler = onBlur;

      // Only start interval if tab is visible
      if (isVisible) {
        // console.log(
        //   "[Resource Widget] ✅ Starting polling interval (5 minutes)"
        // );
        // Poll every 5 minutes (300000ms) - only when tab is visible
        this.pollingInterval = setInterval(() => {
          const stillVisible = !document.hidden;
          // console.log("[Resource Widget] ⏰ Polling interval tick", {
          //   stillVisible,
          //   visibilityState: document.visibilityState,
          // });
          // Double-check visibility before making requests
          if (stillVisible) {
            // console.log("[Resource Widget] 🔄 Making API calls from interval");
            this.reloadAllComponents();
          } else {
            // console.log(
            //   "[Resource Widget] ⏸️ Skipping API calls - tab not visible"
            // );
          }
        }, 5 * 60 * 1000);
      } else {
        // console.log(
        //   "[Resource Widget] ⏸️ Not starting interval - tab not visible"
        // );
      }
    },
    reloadAllComponents() {
      // console.log("[Resource Widget] 📡 reloadAllComponents() called");
      // Trigger reload in child components
      if (
        this.$refs.events &&
        typeof this.$refs.events.reloadEvents === "function"
      ) {
        // console.log("[Resource Widget] 🔄 Reloading Events component");
        this.$refs.events.reloadEvents();
      } else {
        // console.log("[Resource Widget] ⚠️ Events ref not available");
      }
      if (
        this.$refs.status &&
        typeof this.$refs.status.reloadStatus === "function"
      ) {
        // console.log("[Resource Widget] 🔄 Reloading Status component");
        this.$refs.status.reloadStatus();
      } else {
        // console.log("[Resource Widget] ⚠️ Status ref not available");
      }
    },
    stopPolling() {
      // console.log(
      //   "[Resource Widget] 🛑 stopPolling called - clearing interval only (keeping listeners)"
      // );
      // Only clear the interval, but keep event listeners attached
      // so they can detect when tab becomes visible again
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
        // console.log("[Resource Widget] ✅ Interval cleared");
      }
    },
  },
};
</script>
